import { FC, useId } from 'react';

export const MAP_PIN_SIZE = { width: 40, height: 59 } as const;
/** Anchor at the pin tip (not the bottom of the shadow under the tip) */
export const MAP_PIN_ANCHOR = { x: 20, y: 51 } as const;
export const MAP_CIRCLE_SIZE = { width: 44, height: 44 } as const;
export const MAP_CIRCLE_ANCHOR = { x: 22, y: 22 } as const;

interface MapPinProps {
  color?: string,
  /** "mapmarker" (default) or "circle" */
  markerStyle?: string,
  className?: string
}

/**
 * Shared map pin graphic used by geographic map and imagemap markers.
 * @returns {JSX.Element} SVG pin
 */
const MapPin: FC<MapPinProps> = ({
  color = "#3F7DF0",
  markerStyle = "mapmarker",
  className
}) => {
  const reactId = useId().replace(/:/g, "");
  const gradId = `pin-grad-${reactId}`;

  if (markerStyle === "circle") {
    return (
      <svg
        className={className}
        width={MAP_CIRCLE_SIZE.width}
        height={MAP_CIRCLE_SIZE.height}
        viewBox="0 0 44 44"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
      >
        <circle cx="22" cy="22" r="18" fill={color} />
        <circle cx="22" cy="22" r="7" fill="#fff" fillOpacity="0.95" />
      </svg>
    );
  }

  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      width={MAP_PIN_SIZE.width}
      height={MAP_PIN_SIZE.height}
      viewBox="0 0 23 34"
      fill={color}
      aria-hidden
    >
      <defs>
        <radialGradient id={gradId} cx="50%" cy="50%" r="50%">
          <stop offset="10%" stopOpacity="0.4" />
          <stop offset="100%" stopOpacity="0.051" />
        </radialGradient>
      </defs>
      <ellipse cx="11.5" cy="29.54" rx="8.944" ry="4.457" opacity="0.805" fill={`url(#${gradId})`} />
      <path d="M23,11.461c0,4.729-5.75,11.461-10.435,17.828a1.209,1.209,0,0,1-2.13,0C5.75,22.921,0,16.317,0,11.461a11.5,11.5,0,0,1,23,0Z" />
      <path
        d="M11.5,0A11.476,11.476,0,0,0,0,11.461c0,4.856,5.75,11.461,10.435,17.828a1.2,1.2,0,0,0,2.13,0C17.25,22.921,23,16.189,23,11.461A11.476,11.476,0,0,0,11.5,0Zm0,.849A10.612,10.612,0,0,1,22.148,11.461c0,2.037-1.278,4.822-3.22,7.844s-4.694,6.282-7.053,9.483a4.883,4.883,0,0,1-.375.45,4.882,4.882,0,0,1-.375-.45c-2.368-3.209-4.813-6.452-6.849-9.457S.852,13.541.852,11.461A10.612,10.612,0,0,1,11.5.849Z"
        opacity="0.25"
      />
      <ellipse cx="11.5" cy="11.461" rx="4.685" ry="4.669" fill="#fff" />
    </svg>
  );
};

/**
 * Build pin HTML for Leaflet DivIcon (same artwork as MapPin).
 * @param color Marker fill color
 * @param markerStyle Pin style from CMS
 * @returns HTML string
 */
export const buildMapPinHtml = (color: string, markerStyle: string = "mapmarker") => {
  if (markerStyle === "circle") {
    return `
      <div class="map-marker-pin circle">
        <svg width="${MAP_CIRCLE_SIZE.width}" height="${MAP_CIRCLE_SIZE.height}" viewBox="0 0 44 44" xmlns="http://www.w3.org/2000/svg">
          <circle cx="22" cy="22" r="18" fill="${color}"/>
          <circle cx="22" cy="22" r="7" fill="#fff" fill-opacity="0.95"/>
        </svg>
      </div>`;
  }

  const gradId = `pin-grad-${String(color).replace(/[^a-zA-Z0-9]/g, "")}`;

  return `
    <div class="map-marker-pin">
      <svg xmlns="http://www.w3.org/2000/svg" width="${MAP_PIN_SIZE.width}" height="${MAP_PIN_SIZE.height}" viewBox="0 0 23 34" fill="${color}">
        <defs>
          <radialGradient id="${gradId}" cx="50%" cy="50%" r="50%">
            <stop offset="10%" stop-opacity="0.4"/>
            <stop offset="100%" stop-opacity="0.051"/>
          </radialGradient>
        </defs>
        <ellipse cx="11.5" cy="29.54" rx="8.944" ry="4.457" opacity="0.805" fill="url(#${gradId})"/>
        <path d="M23,11.461c0,4.729-5.75,11.461-10.435,17.828a1.209,1.209,0,0,1-2.13,0C5.75,22.921,0,16.317,0,11.461a11.5,11.5,0,0,1,23,0Z"/>
        <path d="M11.5,0A11.476,11.476,0,0,0,0,11.461c0,4.856,5.75,11.461,10.435,17.828a1.2,1.2,0,0,0,2.13,0C17.25,22.921,23,16.189,23,11.461A11.476,11.476,0,0,0,11.5,0Zm0,.849A10.612,10.612,0,0,1,22.148,11.461c0,2.037-1.278,4.822-3.22,7.844s-4.694,6.282-7.053,9.483a4.883,4.883,0,0,1-.375.45,4.882,4.882,0,0,1-.375-.45c-2.368-3.209-4.813-6.452-6.849-9.457S.852,13.541.852,11.461A10.612,10.612,0,0,1,11.5.849Z" opacity="0.25"/>
        <ellipse cx="11.5" cy="11.461" rx="4.685" ry="4.669" fill="#fff"/>
      </svg>
    </div>`;
};

export default MapPin;
